/**
 * Navigation Entity Schemas
 *
 * Canonical Zod schemas for navigation menu items, tile items, and tile grid
 * configuration. Shared between backend, web admin, and mobile app.
 *
 * Field shapes mirror `apps/backend/src/api/_core/types/config.ts`
 * (`NavigationItem`, `TileItem`, `TileGridConfig`) with two intentional
 * adjustments for `TileItemSchema`:
 *   - `accessibilityLabel` is optional (renderers fall back to `label`)
 *   - `audience` is required (per tile screen plan field list)
 */

import { z } from 'zod'

/**
 * Navigation Type Schema - Types of navigation actions
 */
export const NavigationTypeSchema = z.enum(['screen', 'web-view', 'external-link', 'page'])
export type NavigationType = z.infer<typeof NavigationTypeSchema>

/**
 * User Role Schema - Audience types for navigation items
 */
export const UserRoleSchema = z.enum(['parent', 'student', 'faculty', 'staff', 'admin', 'community'])
export type UserRole = z.infer<typeof UserRoleSchema>

/**
 * Tile audience role enum - the roles supported on tile surfaces.
 * Tile audience excludes `admin` (admin status is per-school metadata, not a tile filter).
 * `community` is a member with no role flags at all (alumni, past parents, trustees).
 */
export const TileAudienceRoleSchema = z.enum(['parent', 'student', 'faculty', 'staff', 'community'])
export type TileAudienceRole = z.infer<typeof TileAudienceRoleSchema>

/**
 * The full tile-audience set — a tab covering all of these is visible to everyone.
 *
 * Deliberately the four core roles, not `TileAudienceRoleSchema.options`: tabs
 * saved with `['parent','student','faculty','staff']` must still count as
 * all-audiences, or the backend start-screen guard would start refusing saves.
 * `community` is not part of "everyone".
 */
export const TILE_AUDIENCE_ROLES = [
  'parent',
  'student',
  'faculty',
  'staff'
] as const satisfies readonly TileAudienceRole[]

/**
 * Whether a navigation tab is visible to EVERY user — the "all-audiences" test
 * the home base (`globalFeatures.startScreen`) relies on (R4). The home base
 * must be all-audiences so no user is stranded at boot with no reachable
 * landing, and this single predicate is what keeps the backend write guard, the
 * mobile resolver, and the web picker in lockstep.
 *
 * All-audiences means EITHER no audience restriction (empty/absent) OR an
 * audience that covers every tile role (`TILE_AUDIENCE_ROLES`). The second arm
 * matters because nav is commonly seeded with an EXPLICIT full array (e.g.
 * `['parent','student','faculty','staff']`) rather than `[]`; functionally that
 * is everyone, and treating it as restricted would force needless auto-promotion
 * of tabs already visible to all. Requiring the FULL set is deliberately
 * conservative — it is a superset of any real user-role universe, so a genuinely
 * restricted (partial) audience is never mistaken for all-audiences, and no user
 * is ever stranded. `admin` is excluded by design (not a tile audience).
 */
export function isAllAudiencesTab(audience?: readonly string[] | null): boolean {
  if (!audience || audience.length === 0) return true
  return TILE_AUDIENCE_ROLES.every(role => audience.includes(role))
}

/**
 * Navigation Item Base Schema — the raw `ZodObject` shape.
 *
 * Exported separately from `NavigationItemSchema` (the validated form) so
 * consumers can compose with `.pick()`, `.extend()`, etc. — methods that
 * exist on `ZodObject` but NOT on the `ZodEffects` returned by
 * `.superRefine()`. The web menu manager's form Zod is derived from this
 * base via `.pick()` so the form schema stays in lockstep with the canonical
 * shape (see Plan 003).
 *
 * For runtime validation, use `NavigationItemSchema` (below) — that's the
 * version with the `pageSlug`-required-when-page refinement.
 */
export const NavigationItemBaseSchema = z.object({
  id: z.string().describe('Unique identifier for the navigation item'),
  slug: z.string().describe('URL-friendly slug for the navigation item'),
  icon: z.string().describe('Icon name'),
  label: z.string().describe('Display label'),
  order: z.number().describe('Sort order for the item'),
  visible: z.boolean().describe('Whether the item is visible'),
  navigationType: NavigationTypeSchema.describe('Type of navigation'),
  accessibilityLabel: z.string().describe('Accessibility label for screen readers'),
  // Optional core fields
  screenType: z
    .enum(['feature', 'custom'])
    .optional()
    .describe('Screen type - feature for predefined screens, custom for web-view'),
  featureSlug: z.string().optional().describe('Feature slug for screen navigation type'),
  url: z.string().optional().describe('URL for web-view navigation items'),
  pageSlug: z.string().optional().describe('Page slug for navigationType=page'),
  title: z.string().optional().describe('Title to display in the header'),
  subtext: z.string().optional().describe('Additional descriptive text'),
  // New fields
  status: z.enum(['draft', 'preview', 'beta', 'released']).optional().describe('Development status of the feature'),
  audience: z.array(UserRoleSchema).optional().describe('Target audience for this menu item'),
  // Legacy fields (backwards compatibility)
  roles: z
    .array(UserRoleSchema)
    .optional()
    .describe('User types who can see this item (alias for audience - backwards compatibility)'),
  betaOnly: z.boolean().optional().describe('Whether the item is only visible to beta users'),
  visibility: z.array(UserRoleSchema).optional().describe('User types who can see this item')
})

/**
 * Navigation Item Schema - Core navigation menu item shared by tabs/drawer.
 * Aligns with `NavigationItem` in `apps/backend/src/api/_core/types/config.ts`.
 *
 * Wraps `NavigationItemBaseSchema` with a `.superRefine` that requires
 * `pageSlug` when `navigationType === 'page'`. Use this for runtime
 * validation; use `NavigationItemBaseSchema` if you need to compose
 * (`.pick()`, `.extend()`).
 */
export const NavigationItemSchema = NavigationItemBaseSchema.superRefine((value, ctx) => {
  if (value.navigationType === 'page' && (!value.pageSlug || value.pageSlug.length === 0)) {
    ctx.addIssue({
      code: 'custom',
      message: 'pageSlug is required when navigationType is "page"',
      path: ['pageSlug']
    })
  }
})

export type NavigationItem = z.infer<typeof NavigationItemSchema>

/**
 * Screen Header Config Schema - the floating glass header bar (hamburger/back
 * on the left, chat + notification bell on the right) overlaid on a screen's
 * blurred top band. Defined standalone so content pages can adopt the same
 * shape later; the tile surface and the ICAL_FEED page surface consume it.
 *
 * ⚠️ POLARITY IS THE ADOPTING SURFACE'S CALL, NOT THIS SHAPE'S. The object only
 * says what `enabled: true|false` mean; what ABSENT means is decided where the
 * key is adopted, because the safe answer is whatever that surface already does
 * today. On tile screens absent ⇒ no bar (they shipped opt-in). On ICAL_FEED
 * pages absent ⇒ the bar RENDERS, because every such page renders it today and
 * absent-means-off would strip chrome from live pages on an app update
 * (SCH-1509; `ICAL_FEED_SHOW_HEADER_DEFAULT` in `ical-feed.ts` is that surface's
 * single answer). Never backfill this key on the tile surface, where doing so
 * would change what a page shows.
 */
export const ScreenHeaderConfigSchema = z.object({
  enabled: z.boolean().describe('Render the floating glass header bar over this screen')
})

export type ScreenHeaderConfig = z.infer<typeof ScreenHeaderConfigSchema>

/**
 * Tile Grid Config Schema - Controls grid layout behavior.
 * Used by react-native-super-grid on the mobile client.
 */
export const TileGridConfigSchema = z.object({
  itemDimension: z.number().min(50).max(500).optional().describe('Minimum tile size in pixels (default: 120)'),
  spacing: z.number().min(0).max(50).optional().describe('Spacing between tiles in pixels (default: 10)'),
  maxItemsPerRow: z.number().min(1).max(6).optional().describe('Optional max items per row'),
  backgroundImage: z.url().optional().describe('URL for full-screen background image behind the grid'),
  backgroundColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .optional()
    .describe('Background color in hex format for the screen (fallback if no image)'),
  backgroundOverlayStrength: z
    .number()
    .min(0)
    .max(1)
    .optional()
    .describe('Neutral wash strength over the background image; 0 = image at full strength, higher = more dim'),
  header: ScreenHeaderConfigSchema.optional().describe('Floating glass header bar; absent = not rendered')
})

export type TileGridConfig = z.infer<typeof TileGridConfigSchema>

/**
 * Tile Item Schema - A single tile in a tile screen or tile widget grid.
 *
 * Field shape matches backend `TileItem` (which `extends NavigationItem`) with
 * two plan-driven adjustments:
 *   - `accessibilityLabel` is optional here (mobile renderer falls back to `label`)
 *   - `audience` is required (every tile must declare its target roles)
 *
 * Defined directly rather than via `NavigationItemSchema.extend(...)` so the
 * required/optional shape can diverge from the parent navigation schema.
 *
 * Exported as a base `ZodObject` (`TileItemBaseSchema`) plus a validated form
 * (`TileItemSchema`) that adds the `pageSlug`-required-when-page refinement —
 * same split as `NavigationItem*` so consumers can still `.pick()`/`.extend()`
 * the base if needed.
 */
export const TileItemBaseSchema = z.object({
  // Core navigation fields
  id: z.string().describe('Unique identifier for the tile'),
  slug: z.string().describe('URL-friendly slug for the tile'),
  icon: z.string().describe('Icon name'),
  label: z.string().describe('Display label'),
  order: z.number().describe('Sort order for the tile'),
  visible: z.boolean().describe('Whether the tile is visible'),
  navigationType: NavigationTypeSchema.describe('Type of navigation'),
  audience: z.array(TileAudienceRoleSchema).describe('Audience roles that can see this tile'),
  accessibilityLabel: z
    .string()
    .optional()
    .describe('Accessibility label for screen readers; falls back to `label` at render time'),
  // Optional navigation fields (mirrors NavigationItem)
  screenType: z.enum(['feature', 'custom']).optional().describe('Screen type for screen-navigation tiles'),
  featureSlug: z.string().optional().describe('Feature slug for screen navigation type'),
  url: z.string().optional().describe('URL for web-view / external-link tiles'),
  pageSlug: z.string().optional().describe('Page slug for navigationType=page tiles'),
  title: z.string().optional().describe('Title to display in the header when navigating'),
  subtext: z.string().optional().describe('Additional descriptive text'),
  status: z.enum(['draft', 'preview', 'beta', 'released']).optional().describe('Development status'),
  roles: z
    .array(UserRoleSchema)
    .optional()
    .describe('User types who can see this tile (alias for audience - backwards compatibility)'),
  betaOnly: z.boolean().optional().describe('Whether the tile is only visible to beta users'),
  visibility: z.array(UserRoleSchema).optional().describe('User types who can see this tile'),
  // Tile-specific display fields
  fullWidth: z.boolean().optional().describe('Span full row width (maps to _fullWidth in react-native-super-grid)'),
  backgroundImage: z.url().optional().describe('URL for tile background image'),
  backgroundColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .optional()
    .describe('Tile background color in hex format'),
  backgroundOpacity: z
    .number()
    .min(0)
    .max(1)
    .optional()
    .describe('Opacity for tile background (0-1), allows screen background to show through'),
  textColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .optional()
    .describe('Text color in hex format for label'),
  iconPosition: z
    .enum(['top', 'left', 'overlay', 'hidden'])
    .optional()
    .describe('Icon placement within the tile (default: top)'),
  iconColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .optional()
    .describe('Icon color in hex format (useful with backgrounds)')
})

/**
 * Tile Item Schema — validated form. Wraps `TileItemBaseSchema` with a
 * `.superRefine` requiring `pageSlug` when `navigationType === 'page'`,
 * mirroring `NavigationItemSchema`. Use for runtime validation; use
 * `TileItemBaseSchema` to compose (`.pick()`, `.extend()`).
 */
export const TileItemSchema = TileItemBaseSchema.superRefine((value, ctx) => {
  if (value.navigationType === 'page' && (!value.pageSlug || value.pageSlug.length === 0)) {
    ctx.addIssue({
      code: 'custom',
      message: 'pageSlug is required when navigationType is "page"',
      path: ['pageSlug']
    })
  }
})

export type TileItem = z.infer<typeof TileItemSchema>
